import React from 'react'
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import { Link, graphql } from 'gatsby'
import axios from 'axios'
import qs from 'query-string'
import { useCookies } from 'react-cookie'
import { useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import format from 'date-fns/format'
import isBefore from 'date-fns/isBefore'

import Layout from '../components/Layout'
import Leaderboard from '../components/Leaderboard'
import PreviewCompatibleImage from '../components/PreviewCompatibleImage'

const GET_ATHLETE = gql`
  query Athlete($id: Int!) {
    athleteById(athleteId: $id) {
      athleteId
      info {
        firstName
        lastName
        city
        state
        profilePic
      }
      competesIn
    }
  }
`

export const ChallengePostTemplate = ({
  challengeId,
  challenge,
  helmet,
  metadata
}) => {
  // For local debugging
  //challengeId = "887532f3-ffdb-5d2e-9e3f-085f7f1a2d34"

  // Set the challengeId in the challenge object so we can pass it around as one concept
  challenge.id = challengeId

  // This helps get around the Gatsby/Netlify/WebPack SSR where window is not available
  const queryString = typeof window !== 'undefined' ? qs.parse(window.location.search) : {}
  const [cookies, setCookie ] = useCookies(['athlete']);
  const [athleteId, setAthleteId] = useState(-1);
  const [athlete, setAthlete] = useState();

  let actionButton = (
    <a href={`${metadata.joinChallengeUrl}?state=${challengeId}`}>
      <button className="button is-link">
        Join the Challenge
      </button>
    </a>
  )

  const leaveChallenge = async (cid, aid) => {
    console.log(`Leaving via ${metadata.leaveChallengeUrl}`)
    const result = await axios.post(metadata.leaveChallengeUrl, {
      challengeId: cid,
      athleteId: aid
    })
    console.log(result)
    setAthlete(null)
  }

  const renderDate = (date) => format(new Date(date), "EEEE MMMM dd, yyyy")


  // Setup the athlete id
  useEffect(() => {
    console.log("Setting up athlete id")
    if (cookies.athlete && athleteId < 0) {
      setAthleteId(cookies.athlete)
    }
    // If we were sent back from a signup, we will have a joined key
    else if (queryString.joined) {
      setAthleteId(queryString.athleteId)
      setCookie('athlete', athleteId)
    }
  }, [cookies.athlete, setCookie, queryString.joined, queryString.athleteId, athleteId]);

  // Get the athlete profile  
  console.log('Loading athlete')
  const { error: aError, data } = useQuery(GET_ATHLETE, {
    variables: { id: Number(athleteId) },
    skip: !athleteId,
    ssr: false,
    onCompleted: data => setAthlete(data.athleteById)
  });
  console.log(`Error finding athlete: ${aError}`)
  console.log(data)

  if (athlete) {
    console.log(athlete)
    console.log('Check if the athlete is in this challenge')
    if (athlete.competesIn.includes(challengeId)) {
      actionButton = (
        <button className="button is-link" onClick={() => leaveChallenge(challengeId, athleteId)}>
          Leave the Challenge
        </button>
      )
    }  
  }  

  const units = {
    distance: (challenge.units === "miles") ? 'mi' : 'km',
    elevation: (challenge.units === "miles")  ? 'ft' : 'm',
    time: ''
  }

  const challengeActive = isBefore(Date.now(), new Date(challenge.enddate))

  return (
    <section className="section">
      {helmet || ''}
      <div className="container content">
        <div className="columns">
          <div className="column is-10 is-offset-1">
            <h1 className="title is-size-2 has-text-weight-bold is-bold-light">
              {challenge.title}
            </h1>
            <p><Link to="/challenge">&lt;&lt; Back to Challenges</Link></p>
            <PreviewCompatibleImage imageInfo={{image: challenge.image}} />
            <h3>{ renderDate(challenge.startdate) } - { renderDate(challenge.enddate) }</h3>
            <p>{challenge.description}</p>
            <h4 className="is-size-4 has-text-weight-bold is-bold-light">Measures</h4>
            <ul>
              { challenge.measures.map((measure, i) =>(
                <li ley={i}>{measure.name} - {measure.value.toLocaleString()} {units[measure.type]}</li>
              ))}
            </ul>
            <p></p>
            <p>{ challengeActive && actionButton }</p>
            <Leaderboard challenge={challenge}/>
          </div>
        </div>
      </div>
    </section>
  )
}

ChallengePostTemplate.propTypes = {
  challenge: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    startdate: PropTypes.string,
    enddate: PropTypes.string,
    image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),      
  }),
  metadata: PropTypes.object,
  helmet: PropTypes.object,
}

const ChallengePost = ({ data }) => {
  const { site, markdownRemark: post } = data
  console.log(post)
  return (
    <Layout>
      <ChallengePostTemplate
        challengeId={post.id}
        challenge={post.frontmatter}
        metadata={site.siteMetadata}
        helmet={
          <Helmet titleTemplate="%s | Challenge">
            <title>{`${post.frontmatter.title}`}</title>
            <meta
              name="description"
              content={`${post.frontmatter.description}`}
            />
          </Helmet>
        }
        title={post.frontmatter.title}
      />
    </Layout>
  )
}

ChallengePost.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object,
  }),
}

export default ChallengePost

export const pageQuery = graphql`
  query ChallengePostByID($id: String!) {
    site {
      siteMetadata {
        joinChallengeUrl
        leaveChallengeUrl
      }
    }
    markdownRemark(id: { eq: $id }) {
      id
      html
      frontmatter {
        title
        description
        startdate
        enddate
        units
        image {
          childImageSharp {
            fluid(maxWidth: 2048, quality: 100) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        measures {
          name
          type
          value
        }
      }
    }
  }
`
